import type { ExportV1 } from "@strust/contracts";
import {
  Badge,
  Button,
  Dialog,
  DownloadIcon,
  EmptyState,
  Skeleton,
  TableScroll,
  type BadgeTone,
} from "@strust/ui";
import { css } from "@strust/ui/css";
import { link, table } from "@strust/ui/recipes";
import { useId } from "react";
import { useIntl } from "react-intl";
import { ApiProblem } from "../../../shared/access-gate.tsx";
import { useFormatters } from "../../../shared/use-formatters.ts";
import { exportDownloadHref } from "../api/endpoints.ts";
import { useExportsQuery } from "../api/queries.ts";
import { exportKindMessages, exportMessages, exportStatusMessages } from "../messages.ts";

const NONE = "—";

const TABLE = table({ density: "compact" });
const STACK = css({ display: "flex", flexDirection: "column", gap: "4" });
const HINT = css({ textStyle: "bodySm", color: "text.2", m: "0" });
const DOWNLOAD = css({ display: "inline-flex", alignItems: "center", gap: "1.5" });

const TONES: Record<ExportV1["status"], BadgeTone> = {
  requested: "info",
  running: "info",
  ready: "allow",
  failed: "deny",
  expired: "neutral",
};

/** A file past its `expires_at` is treated as expired even before the API says so. */
export function effectiveStatus(item: ExportV1, now: number): ExportV1["status"] {
  if (item.status === "ready" && item.expires_at && Date.parse(item.expires_at) <= now) {
    return "expired";
  }
  return item.status;
}

export interface ExportsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

/**
 * The caller's exports with status and a download action (spec explorer-user-list, "Export").
 * Only a `ready` export offers the download: the API re-checks owner, operator and permission and
 * answers `export_not_ready` (409) or `export_expired` (410) otherwise.
 */
export function ExportsDialog({ open, onClose }: ExportsDialogProps) {
  const intl = useIntl();
  const format = useFormatters();
  const query = useExportsQuery(open);
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  // The moment the list was fetched: a file that expires later is still ready at that time.
  const now = query.dataUpdatedAt;
  const captionId = useId();

  return (
    <Dialog
      open={open}
      title={intl.formatMessage(exportMessages.dialogTitle)}
      closeLabel={intl.formatMessage(exportMessages.dialogClose)}
      size="lg"
      onClose={onClose}
    >
      <div className={STACK}>
        <p className={HINT}>{intl.formatMessage(exportMessages.dialogDescription)}</p>
        {query.isPending ? (
          <div aria-busy="true">
            <Skeleton height="4rem" />
          </div>
        ) : query.isError ? (
          <ApiProblem
            error={query.error}
            onRetry={() => {
              void query.refetch();
            }}
          />
        ) : items.length === 0 ? (
          <EmptyState title={intl.formatMessage(exportMessages.empty)} />
        ) : (
          <>
            <TableScroll labelledBy={captionId}>
              <table className={TABLE.table}>
                <caption id={captionId} className={TABLE.caption}>
                  {intl.formatMessage(exportMessages.tableCaption)}
                </caption>
                <thead className={TABLE.head}>
                  <tr>
                    <th scope="col" className={TABLE.headerCell}>
                      {intl.formatMessage(exportMessages.colKind)}
                    </th>
                    <th scope="col" className={TABLE.headerCell}>
                      {intl.formatMessage(exportMessages.colRequested)}
                    </th>
                    <th scope="col" className={TABLE.headerCell}>
                      {intl.formatMessage(exportMessages.colStatus)}
                    </th>
                    <th scope="col" className={TABLE.headerCell}>
                      {intl.formatMessage(exportMessages.colRows)}
                    </th>
                    <th scope="col" className={TABLE.headerCell}>
                      {intl.formatMessage(exportMessages.colExpires)}
                    </th>
                    <th scope="col" className={TABLE.headerCell}>
                      {intl.formatMessage(exportMessages.colAction)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const status = effectiveStatus(item, now);
                    return (
                      <tr key={item.export_id} className={TABLE.row}>
                        <td className={TABLE.cell}>
                          {intl.formatMessage(exportKindMessages[item.kind])}
                        </td>
                        <td className={TABLE.cell}>{format.dateTime(item.requested_at)}</td>
                        <td className={TABLE.cell}>
                          <Badge size="sm" tone={TONES[status]}>
                            {intl.formatMessage(exportStatusMessages[status])}
                          </Badge>
                        </td>
                        <td className={TABLE.cell}>
                          {item.row_count === null ? NONE : format.number(item.row_count)}
                        </td>
                        <td className={TABLE.cell}>
                          {item.expires_at ? format.dateTime(item.expires_at) : NONE}
                        </td>
                        <td className={TABLE.cell}>
                          {status === "ready" ? (
                            <a
                              href={exportDownloadHref(item.export_id)}
                              className={`${link()} ${DOWNLOAD}`}
                            >
                              <DownloadIcon />
                              {intl.formatMessage(exportMessages.download)}
                            </a>
                          ) : (
                            NONE
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableScroll>
            {query.hasNextPage ? (
              <Button
                visual="secondary"
                loading={query.isFetchingNextPage}
                onClick={() => {
                  void query.fetchNextPage();
                }}
              >
                {intl.formatMessage(exportMessages.loadMore)}
              </Button>
            ) : null}
          </>
        )}
      </div>
    </Dialog>
  );
}
